import I18n from 'i18n-js';
import React from 'react';
import { connect } from '@reverbdotcom/commons/src/connect';
import * as elog from '@reverbdotcom/commons/src/elog';

import { HeartFilledIcon } from '@reverbdotcom/cadence/icons/react';
import { HeartEmptyIcon } from '@reverbdotcom/cadence/icons/react';
import { RCIcon } from '@reverbdotcom/cadence/components/RCIcon/RCIcon';
import { RCToast, RCFavoriteButton } from '@reverbdotcom/cadence/components';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import classNames from 'classnames';
import { Paths } from '../shared/url_helpers';
import { ToastCloseType } from '@reverbdotcom/cadence/components/types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { buildClickedRemoveFavoriteSearch, buildClickedToastMessage } from '@reverbdotcom/commons/src/event_tracking/favorites';
import { EntityType, EntityTypeName } from '@reverbdotcom/commons/src/favorites/entity_type';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import { DeleteMyFavoriteMutationFn, useDeleteMyFavorite, withDeleteMyFavoriteMutation } from './delete_my_favorite_mutation';
import { useFindFavorite } from './find_favorite_context';
import { CoreFindFavorite } from '../../../src/schemas/rql';
import { FavoriteCacheTypes, setFavoritesCacheStale } from '@reverbdotcom/commons/src/favorites/favorites_hub_cache_utils';

export interface ExternalProps {
  openModal: () => void;
  renderWithBanner: boolean;

  disabled: boolean; // Remove as a prop and replace with `!useFindFavorite().canFavorite` when FollowButtonContainer is deprecated
  isFavorited: boolean; // Remove as a prop and replace with `useFindFavorite().isFavorite` when FollowButtonContainer is deprecated
  entityType: EntityTypeName; // Remove as a prop and replace with `mapFavoriteToEntityType(useFindFavorite().favorite)` when FollowButtonContainer is deprecated
  hasOwnToast?: boolean; // Remove when FollowButtonContainer is deprecated
  toastOpen?: boolean; // Remove when FollowButtonContainer is deprecated
  hideToast?: () => void; // Remove when FollowButtonContainer is deprecated
  condensed?: boolean;
}

interface MutationProps {
  deleteMyFavorite: DeleteMyFavoriteMutationFn;
}

export type Props = ExternalProps & MutationProps;

export function InternalFavoriteButton(props: Props) {
  const url = useURL();
  const { favorite, setFavorite, setIsToastOpen } = useFindFavorite();
  const { deleteMyFavorite } = useDeleteMyFavorite(props.deleteMyFavorite, favorite.id);

  const handleClick = async function (e) {
    e.preventDefault();

    if (props.disabled) {
      return;
    }

    if (props.isFavorited) {
      try {
        await deleteMyFavorite();
        const newFavorite: CoreFindFavorite.Favorite = {
          ...favorite,
          favorited: false,
        };
        setFavorite(newFavorite);
        trackEvent(buildClickedRemoveFavoriteSearch(newFavorite, url));
        setIsToastOpen(true);
        setFavoritesCacheStale(FavoriteCacheTypes.Search);
      } catch (e) {
        elog.error(
          e.message,
          { componentName: 'FavoriteButton' },
          e,
        );
      }
    } else {
      props.openModal();
    }
  };

  const clickedToastMessageEvent = buildClickedToastMessage(url);
  const handleToastClose = (closeType: ToastCloseType) => {
    props.hideToast();
    if (closeType === 'dismiss' || closeType === 'swipe') {
      trackEvent(clickedToastMessageEvent);
    }
  };

  const buttonType = (props.entityType === EntityType.Product || props.renderWithBanner) ? 'secondary' : 'link';
  const classes = classNames(
    'favorite-button__button',
    {
      'favorite-button__button--link': buttonType === 'link',
      'favorite-button__button--secondary': buttonType === 'secondary',
      'favorite-button__button--favorited': buttonType !== 'link' && props.isFavorited,
      'favorite-button__button--disabled': props.disabled,
    },
  );

  const renderButton = () => {
    if (props.condensed) {
      return (
        <RCFavoriteButton
          favorited={props.isFavorited}
          onClick={handleClick}
          disabled={props.disabled}
        />
      );
    }

    return (
      <button
        className={classes}
        onClick={handleClick}
        type="button"
        data-disabled={props.disabled || null}
      >
        <RCIcon svgComponent={props.isFavorited ? HeartFilledIcon : HeartEmptyIcon} inline />
        {
          props.isFavorited ?
            I18n.t('discovery.favorites.favoriteButton.saved') :
            I18n.t(`discovery.favorites.favoriteButton.save.${props.entityType}`) }
      </button>
    );
  };

  return (
    <>
      { renderButton() }
      {
        props.hasOwnToast &&
        <RCToast
          isOpen={props.toastOpen}
          onClose={handleToastClose}
          text={I18n.t('discovery.favorites.toast.added')}
          theme="watch-add"
          link={
            <CoreLink
              to={Paths.myFavoritesSearches.expand({})}
              clickEvent={clickedToastMessageEvent}
            >
              {I18n.t('cadence.RCToast.linkDefault')}
            </CoreLink>
          }
        />
      }
    </>
  );
}

export const FavoriteButton = connect<ExternalProps>([
  withDeleteMyFavoriteMutation,
])(InternalFavoriteButton);
